import React, { Fragment, ReactElement, useEffect, useRef, useState } from 'react';

import { CROSS_BASELINE_TASK_LINK_SEARCH_ATTRIBUTES } from '../../../../constants/constants';
import { actions as baselineTaskActions } from '../../../../actions/baselineTaskActions';

import Notification from '../../../Common/Notification';
import InputText from '../../../Common/InputText';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import * as types from '../../../../constants/actionTypes';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Checkbox } from 'primereact/checkbox';
import Icon from '../../../Common/Icon';
import BulkUpdateAttributesDrawer from './BulkUpdateAttributesDrawer';
import '../CrossBaselineSearchPage.scss';

const searchAttributes = [
	{ id: CROSS_BASELINE_TASK_LINK_SEARCH_ATTRIBUTES.LinkURL, name: CROSS_BASELINE_TASK_LINK_SEARCH_ATTRIBUTES.LinkURL },
	{ id: CROSS_BASELINE_TASK_LINK_SEARCH_ATTRIBUTES.LinkName, name: CROSS_BASELINE_TASK_LINK_SEARCH_ATTRIBUTES.LinkName },
];

interface SearchLinkRequestParams {
	searchTerm: string;
	searchAttribute: string;
	pageSize: number;
	pageNumber: number;
}

const DEFAULT_SEARCH_PARAMS: SearchLinkRequestParams = {
	searchTerm: '',
	searchAttribute: CROSS_BASELINE_TASK_LINK_SEARCH_ATTRIBUTES.LinkURL,
	pageSize: 1000,
	pageNumber: 1,
};

interface DataTableProps {
	links: Array<any>;
	checkedLinks: Array<any>;
	totalCount: number;
	allLinksChecked: boolean;
}

const DEFAULT_DATATABLE_PROPS: DataTableProps = {
	links: [],
	checkedLinks: [],
	totalCount: 0,
	allLinksChecked: false,
};

const CrossBaselineTaskLinkSearch = (): ReactElement => {
	const dispatch: Dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState(false);
	const [isFormValid, setIsFormValid] = useState(true);
	const [isBulkUpdate, setIsBulkUpdate] = useState(false);
	const [searchParams, setSearchParams] = useState(DEFAULT_SEARCH_PARAMS);
	const [dataTableResults, setDataTableResults] = useState<DataTableProps>(DEFAULT_DATATABLE_PROPS);

	const searchCrossBaselineLink = async (params: SearchLinkRequestParams) => dispatch(baselineTaskActions.searchCrossBaselineLink(searchParams));

	const onSearchTermChange = (event: any) => {
		const { value } = event.target;
		setSearchParams((prev) => ({
			...prev,
			searchTerm: value,
		}));
	};

	const onSearchAttributeChange = (event: any) => {
		setSearchParams((prev) => ({
			...prev,
			searchAttribute: event,
		}));
	};

	const onPageChange = (event) => {
		setSearchParams((prev) => ({
			...prev,
			pageNumber: event.page + 1,
		}));
	};

	const onComplete = async () => {
		onToggleBulkUpdate();
		await search();
	};

	const onSearch = async (event: any) => {
		event.preventDefault();
		event.stopPropagation();
		await search();
	};

	const onToggleBulkUpdate = () => {
		setIsBulkUpdate((prev) => !prev);
	};

	const validateForm = () => {
		return searchParams.searchTerm && searchParams.searchTerm.trim().length;
	};

	const search = async () => {
		try {
			setIsLoading(true);

			if (validateForm()) {
				setIsFormValid(true);

				const response = await searchCrossBaselineLink(searchParams);

				if (response.type === types.SEARCH_CROSS_BASELINE_LINK_SUCCESS) {
					setDataTableResults({
						links: response.payload.results,
						totalCount: response.payload.totalCount,
						checkedLinks: [],
						allLinksChecked: false,
					});
				} else if (response.type === types.SEARCH_CROSS_BASELINE_LINK_FAILURE) {
					setDataTableResults(DEFAULT_DATATABLE_PROPS);
					Notification('error', 'Error', response.payload.message);
				}
			} else {
				setIsFormValid(false);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onToggleCheckedLink = (taskLink) => {
		const { id } = taskLink;

		const { links } = dataTableResults;
		let checkedLinks: Array<any> = [];

		const newLinks = links.map((item) => {
			let link = { ...item };
			if (item.id === id) {
				link.IsChecked = !item.IsChecked;
			}

			if (link.IsChecked) {
				checkedLinks.push(link);
			}
			return link;
		});

		// If we've manually checked all tasks, also check the master checkbox
		const allLinksChecked = newLinks.length === checkedLinks.length;

		setDataTableResults((prev) => ({
			...prev,
			links: newLinks,
			checkedLinks: checkedLinks,
			allLinksChecked: allLinksChecked,
		}));
	};

	const checkboxTemplate = (rowData) => {
		return <Checkbox name='checkbox' onChange={() => onToggleCheckedLink(rowData)} checked={rowData.IsChecked}></Checkbox>;
	};

	const urlBodyTemplate = (rowData) => {
		return <span style={{ wordWrap: 'break-word' }}>{rowData.url}</span>;
	};

	const onMasterCheckboxToggle = () => {
		// Toggle the master checkbox to checked or unchecked
		const allLinksChecked = !dataTableResults.allLinksChecked;

		// Check or uncheck all baseline tasks, based on its newly-toggled state
		const newLinks = dataTableResults.links.map((item) => {
			return { ...item, IsChecked: allLinksChecked };
		});
		const checkedLinks = allLinksChecked ? newLinks.slice() : [];

		setDataTableResults((prev) => ({
			...prev,
			links: newLinks,
			checkedLinks: checkedLinks,
			allLinksChecked: allLinksChecked,
		}));
	};

	const masterCheckbox = (
		<div>
			<Checkbox name='checkbox' onChange={() => onMasterCheckboxToggle()} checked={dataTableResults.allLinksChecked}></Checkbox>
		</div>
	);

	return (
		<div className='container-fluid p-4 cross-baseline-search-page'>
			<div className='task-list-header'>
				<div className='search-wrapper'>
					<form noValidate className={`d-flex align-items-center gap-3 flex-wrap ${isFormValid ? '' : 'was-validated'}`}>
						<div className='d-flex gap-3 flex-fill flex-wrap'>
							<InputText
								bootstrapClass='search-term'
								name='searchTerm'
								placeholder='Link Search'
								type='text'
								value={searchParams.searchTerm}
								onChange={onSearchTermChange}
								isRequired={true}
								pattern='.*\S+.*'
								errorMessage='Search term is required.'
							/>

							<MultiSelectBox
								name='searchAttribute'
								idField='id'
								valueField='name'
								label=''
								options={searchAttributes}
								onChange={onSearchAttributeChange}
								selectedValues={searchParams.searchAttribute}
								className='search-attribute'
								isMulti={false}
							/>

							<button className='btn btn-primary' onClick={onSearch}>
								Search
							</button>
						</div>
						
						<div className='icon float-end' onClick={onToggleBulkUpdate}>
							<Icon name='pencil-alt' /> Update Attributes
						</div>
					</form>
				</div>
			</div>

			<div className='row'>
				<LoadingIndicator isLoading={isLoading} hideWhileLoading={true} isFullscreen={true}>
					<DataTable
						filterDisplay='row'
						className='p-datatable-sm p-datatable-striped'
						selectionMode='single'
						value={dataTableResults.links}>
						<Column
							field='isChecked'
							filter={true}
							filterElement={masterCheckbox}
							body={checkboxTemplate}
							style={{ width: '5rem', textAlign: 'center' }}
						/>
						<Column field='name' header='Link Abbreviation' sortable={false} />
						<Column field='url' header='URL' sortable={false} body={urlBodyTemplate} />
						<Column field='baselineName' header='Baseline Name' sortable={false} />
						<Column field='baselineTaskName' header='Baseline Task Name' sortable={false} />
					</DataTable>

					{dataTableResults.totalCount > searchParams.pageSize && (
						<Paginator
							rows={searchParams.pageSize}
							totalRecords={dataTableResults.totalCount}
							first={(searchParams.pageNumber - 1) * searchParams.pageSize}
							onPageChange={onPageChange}
						/>
					)}
					<div className='results-count'>Total records: {dataTableResults.totalCount}</div>
				</LoadingIndicator>
			</div>

			{isBulkUpdate && (
				<BulkUpdateAttributesDrawer selectedLinks={dataTableResults.checkedLinks} onCancel={onToggleBulkUpdate} onComplete={onComplete} />
			)}
		</div>
	);
};

export default CrossBaselineTaskLinkSearch;
